import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother.js';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import TextScramble from './TextScramble.js';

/** Composante Konami de TimTools */
export default class Konami {
  constructor(element) {
    this.element = element;
    this.sequence = [
      'arrowup',
      'arrowup',
      'arrowdown',
      'arrowdown',
      'arrowleft',
      'arrowright',
      'arrowleft',
      'arrowright',
      'b',
      'a',
    ];
    this.buffer = [];
    this.pending = false;
    this.activated = false;
    this.destroyed = false;
    this.options = {
      backdropOpacity: 0.62,
      scrambleDuration: 3,
      revealDelay: 1.6,
    };
    this.onKeyDown = this.onKeyDown.bind(this);
    this.init();
  }

  init() {
    if (this.destroyed || this.pending || this.activated) return;
    document.addEventListener('keydown', this.onKeyDown);
  }

  async onKeyDown(event) {
    if (this.destroyed || this.pending || this.activated) return;

    const target = event.target;
    if (
      event.isComposing ||
      target?.isContentEditable ||
      target?.closest?.(
        'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
      )
    ) {
      this.buffer = [];
      return;
    }

    if (
      event.repeat ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      ['Alt', 'Control', 'Meta', 'Shift', 'AltGraph'].includes(event.key)
    )
      return;

    this.buffer.push(event.key.toLowerCase());
    if (this.buffer.length > this.sequence.length) this.buffer.shift();
    if (
      this.buffer.length !== this.sequence.length ||
      !this.sequence.every((key, index) => key === this.buffer[index])
    )
      return;

    this.buffer = [];
    this.pending = true;
    document.removeEventListener('keydown', this.onKeyDown);

    try {
      await document.fonts.ready;
      if (this.destroyed) return;
      this.controller = new AbortController();
      await this.playTransition(this.controller.signal);
      this.activated = true;
    } catch (error) {
      if (error.name !== 'AbortError')
        console.error('Konami: transition failed.', error);
    } finally {
      this.pending = false;
      if (!this.destroyed && !this.activated) this.init();
    }
  }

  async replaceSiteText(signal) {
    const options = { animate: false, signal };
    await TextScramble.replaceText(
      this.element,
      /techniques?\s+d[’']\s*int[eé]gration\s+(?:multim[eé]dia|mutlim[eé]dia)/giu,
      'Création de médias interactifs',
      options,
    );
    await TextScramble.replaceText(this.element, 'TIM', 'CMI', options);
    ScrollTrigger.refresh();
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'konami-transition';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('data-html2canvas-ignore', 'true');
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'konami-transition__backdrop';

    this.message = document.createElement('div');
    this.message.className = 'konami-transition__message';
    this.label = document.createElement('div');
    this.label.className = 'konami-transition__title';
    this.label.textContent = 'TIM';
    this.subtitle = document.createElement('p');
    this.subtitle.className = 'konami-transition__subtitle';
    this.message.append(this.label, this.subtitle);
    this.overlay.append(this.backdrop, this.message);
    // Hors de #smooth-content : le transform de ScrollSmoother ne doit pas déplacer l'écran fixe.
    document.body.append(this.overlay);
  }

  tween(target, vars, signal) {
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
      const abort = () => {
        animation.kill();
        signal.removeEventListener('abort', abort);
        reject(signal.reason);
      };
      const animation = gsap.to(target, {
        ...vars,
        onComplete: () => {
          signal.removeEventListener('abort', abort);
          resolve();
        },
        onInterrupt: () => {
          signal.removeEventListener('abort', abort);
          reject(new DOMException('Transition interrompue.', 'AbortError'));
        },
      });
      signal.addEventListener('abort', abort, { once: true });
    });
  }

  async initGlitch(signal) {
    try {
      const { default: KonamiGlitch } =
        await import('../utils/KonamiGlitch.js');
      signal.throwIfAborted();
      this.glitch = new KonamiGlitch(this.overlay);
      await this.glitch.init({ signal });
      return true;
    } catch (error) {
      this.glitch?.dispose();
      this.glitch = null;
      if (signal.aborted || error.name === 'AbortError') throw error;
      console.warn(
        'Konami : glitch WebGL indisponible, dévoilement sans shader.',
        error,
      );
      return false;
    }
  }

  async playTransition(signal) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await this.replaceSiteText(signal);
      return;
    }

    const smoother = ScrollSmoother.get();
    const wasPaused = smoother?.paused();
    const wasInert = this.element.hasAttribute('inert');

    const overflow = document.body.style.getPropertyValue('overflow');
    const overflowPriority =
      document.body.style.getPropertyPriority('overflow');
    try {
      this.createOverlay();
      smoother?.paused(true);
      document.body.style.setProperty('overflow', 'hidden');
      this.element.setAttribute('inert', '');
      const hasGlitch = await this.initGlitch(signal);
      if (hasGlitch) {
        await this.tween(
          this.glitch.intensity,
          { value: 1, duration: 0.25 },
          signal,
        );
        await this.tween({}, { duration: 0.9 }, signal);
      }

      await this.tween(
        this.backdrop,
        {
          opacity: this.options.backdropOpacity,
          duration: 0.45,
        },
        signal,
      );
      if (hasGlitch && this.glitch.canvas) {
        await Promise.all([
          this.tween(
            this.glitch.intensity,
            { value: 0, duration: 0.4 },
            signal,
          ),
          this.tween(this.glitch.canvas, { opacity: 0, duration: 0.4 }, signal),
        ]);
      }
      // Le dévoilement se fait au-dessus du vrai site, pas d'une capture figée.
      this.glitch?.dispose();
      this.glitch = null;
      await this.tween(
        this.message,
        { opacity: 1, scale: 1, duration: 0.35 },
        signal,
      );
      await TextScramble.scrambleNode(this.label.firstChild, 'CMI', {
        signal,
        duration: this.options.scrambleDuration,
        revealDelay: this.options.revealDelay,
      });
      this.subtitle.textContent = 'Création de médias interactifs';
      await this.replaceSiteText(signal);
      await this.tween({}, { duration: 1 }, signal);
      await this.tween(this.overlay, { opacity: 0, duration: 0.4 }, signal);
    } finally {
      this.glitch?.dispose();
      this.glitch = null;
      this.element.toggleAttribute('inert', wasInert);
      this.overlay?.remove();
      this.overlay = null;
      this.backdrop = null;
      this.message = null;
      this.label = null;
      this.subtitle = null;
      if (overflow)
        document.body.style.setProperty('overflow', overflow, overflowPriority);
      else document.body.style.removeProperty('overflow');
      smoother?.paused(wasPaused);
    }
  }

  destroy() {
    this.destroyed = true;
    this.controller?.abort();
    this.buffer = [];
    document.removeEventListener('keydown', this.onKeyDown);
  }
}
